import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, Package, DollarSign, Percent, Save,
  AlertTriangle, Clock, TrendingUp, Settings, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPackages = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [packages, setPackages] = useState([]);
  const [payoutSettings, setPayoutSettings] = useState({
    fast_payout_days: 7,
    regular_payout_days: 14,
    first_payout_days: 7
  });
  const [tradingRules, setTradingRules] = useState({
    max_daily_loss_percent: 4,
    max_total_loss_percent: 8,
    max_floating_loss_percent: 2,
    profit_target_phase1: 10,
    profit_target_phase2: 5
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [packagesRes, payoutRes, rulesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/packages`, { headers }),
        fetch(`${API_URL}/api/admin/payout-settings`, { headers }),
        fetch(`${API_URL}/api/admin/trading-rules`, { headers })
      ]);
      
      const packagesData = await packagesRes.json();
      const payoutData = await payoutRes.json();
      const rulesData = await rulesRes.json();
      
      setPackages(packagesData.packages || []);
      setPayoutSettings(payoutData);
      setTradingRules(rulesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageChange = (pkgId, field, value) => {
    setPackages(prev => prev.map(pkg => 
      pkg.id === pkgId ? { ...pkg, [field]: parseFloat(value) || 0 } : pkg
    ));
  };

  const savePackage = async (pkg) => {
    setSaving(pkg.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/packages/${pkg.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          price: pkg.price,
          profit_split: pkg.profit_split,
          max_daily_loss: pkg.max_daily_loss,
          max_total_loss: pkg.max_total_loss,
          max_floating_loss: pkg.max_floating_loss,
          profit_target_phase1: pkg.profit_target_phase1,
          profit_target_phase2: pkg.profit_target_phase2
        })
      });
      
      if (response.ok) {
        toast.success(`${pkg.name} updated successfully!`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Update failed');
      }
    } catch (error) {
      toast.error('Failed to update package');
    } finally {
      setSaving(null);
    }
  };

  const savePayoutSettings = async () => {
    setSaving('payout');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/payout-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payoutSettings)
      });
      
      if (response.ok) {
        toast.success('Payout settings updated!');
      } else {
        toast.error('Update failed');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const challengePackages = packages.filter(p => p.type === 'challenge');
  const instantPackages = packages.filter(p => p.type === 'instant');

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="admin-packages">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/admin')}
            className="border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin Dashboard
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">Package & Rules Management</h1>
            <p className="text-gray-400 text-sm">Edit prices, profit splits, and trading rules</p>
          </div>
        </motion.div>

        <Tabs defaultValue="challenges" className="space-y-6">
          <TabsList className="bg-[hsl(220,20%,7%)] border border-white/10">
            <TabsTrigger value="challenges" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <Package className="w-4 h-4 mr-2" />
              Challenge Packages
            </TabsTrigger>
            <TabsTrigger value="instant" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <TrendingUp className="w-4 h-4 mr-2" />
              Instant Funding
            </TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <Shield className="w-4 h-4 mr-2" />
              Trading Rules
            </TabsTrigger>
            <TabsTrigger value="payouts" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500/20 data-[state=active]:to-cyan-500/20">
              <Clock className="w-4 h-4 mr-2" />
              Payout Settings
            </TabsTrigger>
          </TabsList>

          {/* Challenge Packages Tab */}
          <TabsContent value="challenges" className="space-y-4">
            <div className="grid gap-4">
              {challengePackages.map((pkg) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-400" />
                          </div>
                          <div>
                            <CardTitle className="text-white">{pkg.name}</CardTitle>
                            <CardDescription className="text-gray-500">
                              Account Size: {formatCurrency(pkg.account_size)}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Challenge</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Price ($)</Label>
                          <Input
                            type="number"
                            value={pkg.price}
                            onChange={(e) => handlePackageChange(pkg.id, 'price', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Phase 1 Target (%)</Label>
                          <Input
                            type="number"
                            value={pkg.profit_target_phase1}
                            onChange={(e) => handlePackageChange(pkg.id, 'profit_target_phase1', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Phase 2 Target (%)</Label>
                          <Input
                            type="number"
                            value={pkg.profit_target_phase2}
                            onChange={(e) => handlePackageChange(pkg.id, 'profit_target_phase2', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Max Daily Loss (%)</Label>
                          <Input
                            type="number"
                            value={pkg.max_daily_loss}
                            onChange={(e) => handlePackageChange(pkg.id, 'max_daily_loss', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Max Total Loss (%)</Label>
                          <Input
                            type="number"
                            value={pkg.max_total_loss}
                            onChange={(e) => handlePackageChange(pkg.id, 'max_total_loss', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm flex items-center gap-1">
                            Max Floating Loss (%)
                            <AlertTriangle className="w-3 h-3 text-yellow-400" />
                          </Label>
                          <Input
                            type="number"
                            value={pkg.max_floating_loss || 2}
                            onChange={(e) => handlePackageChange(pkg.id, 'max_floating_loss', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button
                          onClick={() => savePackage(pkg)}
                          disabled={saving === pkg.id}
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                        >
                          {saving === pkg.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Changes
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Instant Funding Tab */}
          <TabsContent value="instant" className="space-y-4">
            <div className="grid gap-4">
              {instantPackages.map((pkg) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <CardTitle className="text-white">{pkg.name}</CardTitle>
                            <CardDescription className="text-gray-500">
                              Account Size: {formatCurrency(pkg.account_size)}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Instant</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Price ($)</Label>
                          <Input
                            type="number"
                            value={pkg.price}
                            onChange={(e) => handlePackageChange(pkg.id, 'price', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Profit Split (%)</Label>
                          <Input
                            type="number"
                            value={pkg.profit_split}
                            onChange={(e) => handlePackageChange(pkg.id, 'profit_split', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Max Daily Loss (%)</Label>
                          <Input
                            type="number"
                            value={pkg.max_daily_loss}
                            onChange={(e) => handlePackageChange(pkg.id, 'max_daily_loss', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm">Max Total Loss (%)</Label>
                          <Input
                            type="number"
                            value={pkg.max_total_loss}
                            onChange={(e) => handlePackageChange(pkg.id, 'max_total_loss', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400 text-sm flex items-center gap-1">
                            Max Floating Loss (%)
                            <AlertTriangle className="w-3 h-3 text-yellow-400" />
                          </Label>
                          <Input
                            type="number"
                            value={pkg.max_floating_loss || 2}
                            onChange={(e) => handlePackageChange(pkg.id, 'max_floating_loss', e.target.value)}
                            className="bg-white/5 border-white/10 text-white"
                          />
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button
                          onClick={() => savePackage(pkg)}
                          disabled={saving === pkg.id}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                        >
                          {saving === pkg.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Changes
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Trading Rules Tab */}
          <TabsContent value="rules">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-400" />
                  Trading Rules
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Configure global trading rules and risk parameters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Floating Loss Rule - Highlighted */}
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <h4 className="font-medium text-yellow-400">IMPORTANT: 2% Floating Loss Rule</h4>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    Traders cannot exceed a 2% floating loss at any given time during trading. 
                    Exceeding this limit will result in an <strong className="text-red-400">automatic breach</strong>.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-400 text-sm">Max Floating Loss (%)</Label>
                      <Input
                        type="number"
                        value={tradingRules.max_floating_loss_percent}
                        onChange={(e) => setTradingRules({...tradingRules, max_floating_loss_percent: parseFloat(e.target.value)})}
                        className="bg-white/5 border-yellow-500/30 text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">Max Daily Loss (%)</Label>
                    <Input
                      type="number"
                      value={tradingRules.max_daily_loss_percent}
                      onChange={(e) => setTradingRules({...tradingRules, max_daily_loss_percent: parseFloat(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">Max Total Loss (%)</Label>
                    <Input
                      type="number"
                      value={tradingRules.max_total_loss_percent}
                      onChange={(e) => setTradingRules({...tradingRules, max_total_loss_percent: parseFloat(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">Phase 1 Profit Target (%)</Label>
                    <Input
                      type="number"
                      value={tradingRules.profit_target_phase1}
                      onChange={(e) => setTradingRules({...tradingRules, profit_target_phase1: parseFloat(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">Phase 2 Profit Target (%)</Label>
                    <Input
                      type="number"
                      value={tradingRules.profit_target_phase2}
                      onChange={(e) => setTradingRules({...tradingRules, profit_target_phase2: parseFloat(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-white/10">
                  <h4 className="font-medium text-white mb-3">Rule Summary</h4>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span>Maximum <strong className="text-white">{tradingRules.max_floating_loss_percent}%</strong> floating loss at any time (auto breach)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      <span>Maximum <strong className="text-white">{tradingRules.max_daily_loss_percent}%</strong> daily loss</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      <span>Maximum <strong className="text-white">{tradingRules.max_total_loss_percent}%</strong> total loss</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payout Settings Tab */}
          <TabsContent value="payouts">
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-400" />
                  Payout Cycle Settings
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Configure payout waiting periods
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">First Payout After (days)</Label>
                    <Input
                      type="number"
                      value={payoutSettings.first_payout_days}
                      onChange={(e) => setPayoutSettings({...payoutSettings, first_payout_days: parseInt(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500">Days of trading before first payout request</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">Fast Payout Cycle (days)</Label>
                    <Input
                      type="number"
                      value={payoutSettings.fast_payout_days}
                      onChange={(e) => setPayoutSettings({...payoutSettings, fast_payout_days: parseInt(e.target.value)})}
                      className="bg-white/5 border-cyan-500/30 text-white"
                    />
                    <p className="text-xs text-cyan-400">For traders who opt for faster payouts</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-400 text-sm">Regular Payout Cycle (days)</Label>
                    <Input
                      type="number"
                      value={payoutSettings.regular_payout_days}
                      onChange={(e) => setPayoutSettings({...payoutSettings, regular_payout_days: parseInt(e.target.value)})}
                      className="bg-white/5 border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500">Standard payout cycle</p>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <h4 className="font-medium text-blue-400 mb-2">Current Payout Schedule</h4>
                  <ul className="space-y-1 text-sm text-gray-300">
                    <li>• First payout available after <strong>{payoutSettings.first_payout_days}</strong> trading days</li>
                    <li>• Fast payout option: Every <strong>{payoutSettings.fast_payout_days}</strong> days</li>
                    <li>• Regular payout: Every <strong>{payoutSettings.regular_payout_days}</strong> days</li>
                  </ul>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={savePayoutSettings}
                    disabled={saving === 'payout'}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                  >
                    {saving === 'payout' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Payout Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPackages;
